import { Helmet } from "react-helmet-async";
import { Button } from "@mineoptics/shared-ui";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@mineoptics/shared-ui";
import { Drill, Bomb, DollarSign, TrendingUp, FileText, CheckCircle, ArrowRight, Play, Sparkles, FolderKanban, Calculator, BarChart3, Layers } from "lucide-react";

const features = [{
  icon: Drill,
  title: "Drilling Costs",
  description: "Comprehensive drilling cost model including equipment CAPEX, fuel, labor, consumables, and downtime analysis.",
  href: "/features/drilling"
}, {
  icon: Bomb,
  title: "Blasting Costs",
  description: "Complete blasting economics covering explosives, detonators, crew labor, and equipment depreciation.",
  href: "/features/blasting"
}, {
  icon: DollarSign,
  title: "Revenue Model",
  description: "Accurate revenue modeling with grade reconciliation, commodity pricing, and metallurgical recovery rates.",
  href: "/features/revenue"
}, {
  icon: TrendingUp,
  title: "Cashflow Analysis",
  description: "Multi-year financial projections with CAPEX/OPEX tracking, sensitivity analysis, and operating margins.",
  href: "/features/cashflow"
}, {
  icon: FileText,
  title: "Reporting & Export",
  description: "Executive summaries, detailed cost breakdowns, scenario comparisons, and export to Excel and PDF.",
  href: "/features/reporting"
}];


export default function Home() {
  const APP_URL = import.meta.env.VITE_APP_URL || 'https://app.mineoptics.com';

  return <>
    <Helmet>
      <title>MineOptics | Modern Mining Cost Platform</title>
      <meta
        name="description"
        content="The modern platform for mining cost modeling. Drill & blast optimization, fleet sizing, and operating costs - all in one beautiful interface."
      />
    </Helmet>

    <div className="min-h-screen bg-background">
      {/* Navigation - Modern Glassmorphism */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/10">
        <div className="container mx-auto flex h-16 items-center justify-between px-4 md:px-6">
          <a href="/" className="flex items-center gap-2.5 group">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-accent text-white font-bold text-sm shadow-lg shadow-primary/25 transition-transform group-hover:scale-105">
              M
            </div>
            <span className="font-semibold text-xl tracking-tight">MineOptics</span>
          </a>
          <nav className="hidden md:flex items-center gap-8">
            <a href="#features" className="nav-link text-sm font-medium text-muted-foreground hover:text-foreground transition-colors pb-1">
              Features
            </a>
            <a href="/pricing" className="nav-link text-sm font-medium text-muted-foreground hover:text-foreground transition-colors pb-1">
              Pricing
            </a>
            <a href="https://docs.mineoptics.com" className="nav-link text-sm font-medium text-muted-foreground hover:text-foreground transition-colors pb-1" target="_blank" rel="noopener noreferrer">
              Docs
            </a>
          </nav>
          <div className="flex items-center gap-3">
            <Button variant="ghost" className="hidden sm:inline-flex font-medium" asChild>
              <a href={`${APP_URL}/auth/login`}>Sign in</a>
            </Button>
            <Button className="btn-gradient text-white border-0 shadow-lg shadow-primary/25 font-medium" asChild>
              <a href={`${APP_URL}/auth/signup`}>
                Get Started
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section - Product-Led with Dashboard Preview */}
      <section className="relative min-h-[95vh] flex items-center overflow-hidden pt-16">
        {/* Subtle Background */}
        <div className="absolute inset-0 gradient-bg-subtle" />
        <div className="absolute inset-0 pattern-dots opacity-30" />

        <div className="relative container mx-auto px-4 md:px-6 py-20 md:py-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">

            {/* Left Column - Messaging */}
            <div className="max-w-xl">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-8 fade-in">
                <Sparkles className="w-4 h-4" />
                <span>Trusted by mining operations worldwide</span>
              </div>

              {/* Headline - Problem-Solution Focus */}
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight mb-6 fade-in stagger-1" style={{ lineHeight: '1.1' }}>
                Stop guessing at
                <br />
                mining costs.
                <br />
                <span className="gradient-text">Know exactly.</span>
              </h1>

              {/* Value Proposition */}
              <p className="text-lg md:text-xl text-muted-foreground mb-10 leading-relaxed fade-in stagger-2">
                Real-time cost intelligence from drill to dollar. Model every blast, track every expense, optimize every operation—without the spreadsheet chaos.
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 mb-10 fade-in stagger-3">
                <Button size="lg" className="btn-gradient text-white border-0 shadow-xl shadow-primary/30 font-medium text-base px-8 h-12" asChild>
                  <a href={`${APP_URL}/auth/signup`}>
                    Start Free Trial
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </a>
                </Button>
                <Button size="lg" variant="outline" className="font-medium text-base px-8 h-12 bg-white/50 backdrop-blur-sm hover:bg-white/80">
                  <Play className="mr-2 h-5 w-5" />
                  Watch Demo
                </Button>
              </div>

              {/* Trust Indicators */}
              <div className="flex flex-wrap gap-6 text-sm text-muted-foreground fade-in stagger-4">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-primary" />
                  <span>14-day free trial</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-primary" />
                  <span>No credit card</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-primary" />
                  <span>SOC 2 Compliant</span>
                </div>
              </div>
            </div>

            {/* Right Column - Dashboard Preview with Floating Cards */}
            <div className="relative lg:pl-8">
              {/* Decorative background blob */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] gradient-bg opacity-5 blur-3xl rounded-full" />

              {/* Main Dashboard Card */}
              <div className="relative rounded-2xl shadow-2xl overflow-hidden fade-in stagger-2" style={{ backgroundColor: 'var(--color-ivory)' }}>
                {/* Dashboard Header */}
                <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--color-sand)' }}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white font-bold text-xs">
                      M
                    </div>
                    <div>
                      <div className="font-semibold text-sm" style={{ color: 'var(--color-charcoal)' }}>
                        North Ridge Mine
                      </div>
                      <div className="text-xs" style={{ color: 'var(--color-muted-gray)' }}>
                        Cost Dashboard
                      </div>
                    </div>
                  </div>
                  <div className="text-xs px-3 py-1 rounded-full font-medium" style={{ backgroundColor: 'rgba(183, 216, 122, 0.2)', color: 'var(--color-forest)' }}>
                    Live
                  </div>
                </div>

                {/* Dashboard Content */}
                <div className="p-6 space-y-4">
                  {/* Key Metrics */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 rounded-xl" style={{ backgroundColor: 'var(--color-sand)' }}>
                      <div className="text-xs font-medium mb-1" style={{ color: 'var(--color-muted-gray)' }}>
                        Blast Cost/Tonne
                      </div>
                      <div className="text-2xl font-bold" style={{ color: 'var(--color-charcoal)' }}>
                        $1.84
                      </div>
                      <div className="text-xs mt-1" style={{ color: 'var(--color-sage)' }}>
                        ↓ 12% vs budget
                      </div>
                    </div>
                    <div className="p-4 rounded-xl" style={{ backgroundColor: 'var(--color-sand)' }}>
                      <div className="text-xs font-medium mb-1" style={{ color: 'var(--color-muted-gray)' }}>
                        Monthly Drilling
                      </div>
                      <div className="text-2xl font-bold" style={{ color: 'var(--color-charcoal)' }}>
                        $287K
                      </div>
                      <div className="text-xs mt-1" style={{ color: 'var(--color-sage)' }}>
                        On target
                      </div>
                    </div>
                  </div>

                  {/* Cost Breakdown Chart */}
                  <div className="p-4 rounded-xl" style={{ backgroundColor: 'var(--color-sand)' }}>
                    <div className="text-xs font-semibold mb-3" style={{ color: 'var(--color-charcoal)' }}>
                      Operating Cost Breakdown
                    </div>
                    <div className="space-y-3">
                      {[
                        { label: 'Drilling', amount: '$287K', percent: 42, color: 'var(--color-forest)' },
                        { label: 'Blasting', amount: '$194K', percent: 28, color: 'var(--color-moss)' },
                        { label: 'Haulage', amount: '$152K', percent: 22, color: 'var(--color-sage)' },
                        { label: 'Other', amount: '$54K', percent: 8, color: 'var(--color-lichen)' }
                      ].map((item, idx) => (
                        <div key={idx}>
                          <div className="flex justify-between text-xs mb-1.5">
                            <span style={{ color: 'var(--color-muted-gray)' }}>{item.label}</span>
                            <span className="font-semibold" style={{ color: 'var(--color-charcoal)' }}>{item.amount}</span>
                          </div>
                          <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(31, 35, 32, 0.08)' }}>
                            <div
                              className="h-full rounded-full transition-all duration-1000 ease-out"
                              style={{
                                width: `${item.percent}%`,
                                backgroundColor: item.color
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Recent Activity */}
                  <div className="p-4 rounded-xl" style={{ backgroundColor: 'var(--color-sand)' }}>
                    <div className="text-xs font-semibold mb-3" style={{ color: 'var(--color-charcoal)' }}>
                      Recent Updates
                    </div>
                    <div className="space-y-2">
                      {[
                        { action: 'Blast Pattern Updated', time: '2 min ago' },
                        { action: 'Equipment Cost Synced', time: '15 min ago' }
                      ].map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--color-lime)' }} />
                            <span style={{ color: 'var(--color-charcoal)' }}>{item.action}</span>
                          </div>
                          <span style={{ color: 'var(--color-muted-gray)' }}>{item.time}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Metric Cards */}
              <div className="absolute -top-8 -left-8 rounded-xl p-4 shadow-xl fade-in stagger-3 hidden lg:block"
                   style={{ backgroundColor: 'var(--color-forest)' }}>
                <div className="text-xs font-medium mb-1" style={{ color: 'var(--color-lime)' }}>
                  Powder Factor
                </div>
                <div className="text-2xl font-bold" style={{ color: 'var(--color-ivory)' }}>
                  0.68
                </div>
                <div className="text-xs mt-1" style={{ color: 'var(--color-sage)' }}>
                  kg/m³
                </div>
              </div>

              <div className="absolute -bottom-6 -right-6 rounded-xl p-4 shadow-xl backdrop-blur-sm fade-in stagger-4 hidden lg:block"
                   style={{ backgroundColor: 'rgba(183, 216, 122, 0.95)' }}>
                <div className="text-xs font-medium mb-1" style={{ color: 'var(--color-forest)' }}>
                  Cost Savings YTD
                </div>
                <div className="text-2xl font-bold" style={{ color: 'var(--color-charcoal)' }}>
                  $1.2M
                </div>
                <div className="text-xs mt-1 font-medium" style={{ color: 'var(--color-forest)' }}>
                  ↑ 18% efficiency
                </div>
              </div>

              <div className="absolute top-1/4 -right-4 rounded-xl p-4 shadow-xl fade-in stagger-5 hidden xl:block"
                   style={{ backgroundColor: 'var(--color-ivory)' }}>
                <div className="text-xs font-medium mb-1" style={{ color: 'var(--color-muted-gray)' }}>
                  Fleet Utilization
                </div>
                <div className="text-2xl font-bold" style={{ color: 'var(--color-forest)' }}>
                  94%
                </div>
                <div className="text-xs mt-1" style={{ color: 'var(--color-sage)' }}>
                  Above target
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Features Section - Modern Cards */}
      <section id="features" className="py-24 md:py-32 relative">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-16 md:mb-20">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              <span>Powerful Features</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Everything you need to
              <br />
              <span className="gradient-text">optimize mining costs</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Comprehensive mining cost modeling and engineering tools in one beautiful, intuitive platform
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="feature-card border-0 shadow-soft bg-white/80 backdrop-blur-sm hover-lift">
                  <CardHeader className="pb-6 pt-8 px-8">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/10 to-accent/10 flex items-center justify-center mb-5">
                      <Icon className="w-7 h-7 text-primary" />
                    </div>
                    <CardTitle className="text-xl font-semibold mb-3">{feature.title}</CardTitle>
                    <CardDescription className="text-base leading-relaxed mb-4">
                      {feature.description}
                    </CardDescription>
                    <a href={feature.href} className="text-primary text-sm font-medium inline-flex items-center hover:underline">
                      Learn more
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </a>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Project Management & Cost Modeling - Dark Impact Section */}
      <section className="py-24 md:py-32 relative overflow-hidden" style={{ backgroundColor: 'var(--color-graphite)' }}>
        {/* Subtle pattern overlay */}
        <div className="absolute inset-0 pattern-dots opacity-20" />

        <div className="relative container mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
            {/* Left Column - Content */}
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-8"
                   style={{ backgroundColor: 'rgba(183, 216, 122, 0.15)', color: 'var(--color-lime)' }}>
                <FolderKanban className="w-4 h-4" />
                <span>Project Intelligence</span>
              </div>

              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                  style={{ color: '#EBFC72' }}>
                Manage every site.
                <br />
                Model every dollar.
              </h2>

              <p className="text-lg md:text-xl mb-10 leading-relaxed"
                 style={{ color: 'var(--color-sand)' }}>
                From exploration to production, organize your operations and understand your economics with precision.
                MineOptics gives you clarity on what matters most.
              </p>

              {/* Feature highlights */}
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center"
                       style={{ backgroundColor: 'rgba(183, 216, 122, 0.15)' }}>
                    <Layers className="w-6 h-6" style={{ color: 'var(--color-lime)' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2" style={{ color: 'var(--color-ivory)' }}>
                      Multi-site portfolio management
                    </h3>
                    <p className="text-base leading-relaxed" style={{ color: 'var(--color-muted-gray)' }}>
                      Track all your sites in one dashboard. Compare performance, share best practices, and identify opportunities across your entire operation.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center"
                       style={{ backgroundColor: 'rgba(183, 216, 122, 0.15)' }}>
                    <Calculator className="w-6 h-6" style={{ color: 'var(--color-lime)' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2" style={{ color: 'var(--color-ivory)' }}>
                      Real-time cost intelligence
                    </h3>
                    <p className="text-base leading-relaxed" style={{ color: 'var(--color-muted-gray)' }}>
                      Understand your cost structure instantly. From drill bit to bottom line, every expense is tracked, categorized, and visualized for decision-making.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center"
                       style={{ backgroundColor: 'rgba(183, 216, 122, 0.15)' }}>
                    <BarChart3 className="w-6 h-6" style={{ color: 'var(--color-lime)' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2" style={{ color: 'var(--color-ivory)' }}>
                      Scenario planning made simple
                    </h3>
                    <p className="text-base leading-relaxed" style={{ color: 'var(--color-muted-gray)' }}>
                      Test different operating strategies without spreadsheets. Model equipment changes, commodity shifts, and expansion scenarios in minutes.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Visual Element */}
            <div className="relative">
              {/* Decorative background blob */}
              <div className="absolute -top-10 -right-10 w-80 h-80 rounded-full opacity-10 blur-3xl"
                   style={{ backgroundColor: 'var(--color-lime)' }} />

              {/* Main visual card */}
              <div className="relative rounded-2xl p-8 shadow-2xl"
                   style={{ backgroundColor: 'var(--color-forest)' }}>

                {/* Mock dashboard preview */}
                <div className="space-y-4">
                  {/* Header */}
                  <div className="flex items-center justify-between pb-4 border-b"
                       style={{ borderColor: 'rgba(183, 216, 122, 0.2)' }}>
                    <div>
                      <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-lime)' }}>
                        Portfolio Overview
                      </div>
                      <div className="text-2xl font-bold" style={{ color: 'var(--color-ivory)' }}>
                        $2.4M Monthly Opex
                      </div>
                    </div>
                    <div className="px-3 py-1 rounded-lg text-sm font-medium"
                         style={{ backgroundColor: 'rgba(183, 216, 122, 0.2)', color: 'var(--color-lime)' }}>
                      ↓ 8% vs target
                    </div>
                  </div>

                  {/* Site cards */}
                  <div className="space-y-3">
                    {[
                      { name: 'North Ridge', cost: '$847K', trend: 'up', value: '+3%' },
                      { name: 'East Valley', cost: '$692K', trend: 'down', value: '-12%' },
                      { name: 'South Peak', cost: '$531K', trend: 'down', value: '-5%' }
                    ].map((site, idx) => (
                      <div key={idx}
                           className="rounded-xl p-4 transition-all hover:translate-x-1"
                           style={{ backgroundColor: 'rgba(243, 241, 234, 0.05)' }}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-2 h-2 rounded-full"
                                 style={{ backgroundColor: 'var(--color-lime)' }} />
                            <div>
                              <div className="font-medium text-sm mb-0.5"
                                   style={{ color: 'var(--color-ivory)' }}>
                                {site.name}
                              </div>
                              <div className="text-xs"
                                   style={{ color: 'var(--color-muted-gray)' }}>
                                Operating Cost
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-semibold mb-0.5"
                                 style={{ color: 'var(--color-ivory)' }}>
                              {site.cost}
                            </div>
                            <div className="text-xs font-medium"
                                 style={{ color: site.trend === 'down' ? 'var(--color-lime)' : 'var(--color-muted-gray)' }}>
                              {site.value}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Cost breakdown preview */}
                  <div className="pt-4 mt-2 border-t"
                       style={{ borderColor: 'rgba(183, 216, 122, 0.2)' }}>
                    <div className="text-xs font-medium mb-3"
                         style={{ color: 'var(--color-sage)' }}>
                      Cost Distribution
                    </div>
                    <div className="space-y-2">
                      {[
                        { label: 'Drilling', percent: 38 },
                        { label: 'Blasting', percent: 28 },
                        { label: 'Fleet & Haulage', percent: 22 },
                        { label: 'Other', percent: 12 }
                      ].map((item, idx) => (
                        <div key={idx}>
                          <div className="flex justify-between text-xs mb-1">
                            <span style={{ color: 'var(--color-muted-gray)' }}>{item.label}</span>
                            <span style={{ color: 'var(--color-ivory)' }}>{item.percent}%</span>
                          </div>
                          <div className="h-1.5 rounded-full overflow-hidden"
                               style={{ backgroundColor: 'rgba(243, 241, 234, 0.1)' }}>
                            <div className="h-full rounded-full transition-all"
                                 style={{
                                   width: `${item.percent}%`,
                                   backgroundColor: 'var(--color-lime)'
                                 }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating stat cards */}
              <div className="absolute -bottom-6 -left-6 rounded-xl p-4 shadow-lg backdrop-blur-sm"
                   style={{ backgroundColor: 'rgba(183, 216, 122, 0.95)' }}>
                <div className="text-xs font-medium mb-1" style={{ color: 'var(--color-forest)' }}>
                  Active Sites
                </div>
                <div className="text-2xl font-bold" style={{ color: 'var(--color-charcoal)' }}>
                  12
                </div>
              </div>

              <div className="absolute top-6 -right-6 rounded-xl p-4 shadow-lg"
                   style={{ backgroundColor: 'var(--color-ivory)' }}>
                <div className="text-xs font-medium mb-1" style={{ color: 'var(--color-muted-gray)' }}>
                  Cost Accuracy
                </div>
                <div className="text-2xl font-bold" style={{ color: 'var(--color-forest)' }}>
                  96%
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* CTA Section - Gradient Background */}
      <section id="contact" className="py-24 md:py-32 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 gradient-bg" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />

        <div className="relative container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Ready to transform your
              <br />
              mining operations?
            </h2>
            <p className="text-lg md:text-xl mb-10 text-white/80">
              Join leading mining operations using MineOptics to optimize their costs and streamline their workflows
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-white text-primary hover:bg-white/90 font-medium text-base px-8 h-12 shadow-xl" asChild>
                <a href={`${APP_URL}/auth/signup`}>
                  Start Free Trial
                  <ArrowRight className="ml-2 h-5 w-5" />
                </a>
              </Button>
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 font-medium text-base px-8 h-12 bg-transparent">
                Contact Sales
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer - Clean Modern */}
      <footer className="py-16 md:py-20 bg-muted/30 border-t">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-5 gap-12 md:gap-8">
            <div className="md:col-span-2">
              <a href="/" className="flex items-center gap-2.5 mb-6">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-accent text-white font-bold text-sm shadow-lg shadow-primary/25">
                  M
                </div>
                <span className="font-semibold text-xl tracking-tight">MineOptics</span>
              </a>
              <p className="text-muted-foreground max-w-sm leading-relaxed">
                The modern platform for mining cost modeling. Built for teams who want to work smarter, not harder.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-4 text-foreground">Product</h3>
              <ul className="space-y-3">
                <li><a href="#features" className="text-muted-foreground hover:text-primary transition-colors">Features</a></li>
                <li><a href="/pricing" className="text-muted-foreground hover:text-primary transition-colors">Pricing</a></li>
                <li><a href="https://docs.mineoptics.com" className="text-muted-foreground hover:text-primary transition-colors" target="_blank" rel="noopener noreferrer">Documentation</a></li>
                <li><a href={`${APP_URL}`} className="text-muted-foreground hover:text-primary transition-colors">Dashboard</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4 text-foreground">Company</h3>
              <ul className="space-y-3">
                <li><a href="/contact" className="text-muted-foreground hover:text-primary transition-colors">Contact</a></li>
                <li><a href="/about" className="text-muted-foreground hover:text-primary transition-colors">About</a></li>
                <li><a href="/careers" className="text-muted-foreground hover:text-primary transition-colors">Careers</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4 text-foreground">Legal</h3>
              <ul className="space-y-3">
                <li><a href="/privacy" className="text-muted-foreground hover:text-primary transition-colors">Privacy</a></li>
                <li><a href="/terms" className="text-muted-foreground hover:text-primary transition-colors">Terms</a></li>
                <li><a href="/security" className="text-muted-foreground hover:text-primary transition-colors">Security</a></li>
              </ul>
            </div>
          </div>
          <div className="mt-16 pt-8 border-t flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-muted-foreground">
              © 2024 MineOptics. All rights reserved.
            </p>
            <div className="flex gap-6">
              <a href="#" className="text-muted-foreground hover:text-primary transition-colors">
                <span className="sr-only">Twitter</span>
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" /></svg>
              </a>
              <a href="#" className="text-muted-foreground hover:text-primary transition-colors">
                <span className="sr-only">LinkedIn</span>
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" /></svg>
              </a>
              <a href="#" className="text-muted-foreground hover:text-primary transition-colors">
                <span className="sr-only">GitHub</span>
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" /></svg>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  </>;
}
